import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    NumberField,
    FunctionField,
    CreateButton,
    TopToolbar,
    useRecordContext,
} from 'react-admin';
import { Alert, Box, Chip, Typography } from '@mui/material';
import RosterImport from './RosterImport';

/**
 * Past imports, and the button that starts a new one.
 *
 * WHY THE HISTORY IS A SCREEN AT ALL
 * ----------------------------------
 * Adding one student through a form has an obvious outcome. Loading 800 from a
 * spreadsheet does not: it half-succeeds, it is re-run with a corrected file,
 * and three weeks later somebody asks why two students share a roll number.
 *
 * Without this list the only evidence of what happened is the roster itself,
 * which is the very thing being questioned. So each import keeps its totals,
 * who ran it, and the rows that failed with the reason.
 *
 * The list is the resource's `list` and the wizard is its `create`, so the
 * ordinary react-admin "Create" button opens the importer. That is not a
 * contrivance -- running an import IS creating one of these records.
 */

const StatusChip = () => {
    const record = useRecordContext();
    if (!record) return null;

    const colour: any =
        record.status === 'completed'
            ? 'success'
            : record.status === 'abandoned'
              ? 'warning'
              : 'info';

    return <Chip size="small" label={record.status} color={colour} />;
};

/**
 * The failures, expanded under the row. This is what the screen is for: the
 * question weeks later is never "how many" but "which ones, and why".
 */
const ImportProblems = () => {
    const record = useRecordContext();
    if (!record) return null;

    if (!record.errors || record.errors.length === 0) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    Every row in this file was imported.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
                {record.errors.length} row(s) did not import
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
                {record.errors.map((e: any, i: number) => (
                    <li key={i}>
                        <Typography variant="body2">
                            Row {e.row} {e.login ? `(${e.login})` : ''}: {e.problem}
                        </Typography>
                    </li>
                ))}
            </Box>
            <Typography variant="caption" color="text.secondary">
                Only the login and the reason are kept. Names and guardian details from the file are
                never stored here.
            </Typography>
        </Box>
    );
};

const Actions = () => (
    <TopToolbar>
        <CreateButton label="Import a roster" />
    </TopToolbar>
);

const ImportList = props => (
    <List
        title="Roster imports"
        {...props}
        actions={<Actions />}
        sort={{ field: 'started_at', order: 'DESC' }}
        exporter={false}
        empty={
            <Box sx={{ m: 3 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                    No rosters have been imported yet. You can load students and teachers from a CSV
                    file instead of adding them one at a time.
                </Alert>
                <CreateButton label="Import a roster" variant="contained" />
            </Box>
        }
    >
        <Datagrid bulkActionButtons={false} expand={<ImportProblems />} rowClick={false}>
            <DateField source="started_at" label="When" showTime />
            <TextField source="kind" label="What" />
            <TextField source="filename" label="File" />
            <TextField source="imported_by_name" label="By" />
            <NumberField source="total_rows" label="Rows" />

            <FunctionField
                label="Result"
                render={(r: any) => (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {r.created_count > 0 && (
                            <Chip size="small" color="success" label={`${r.created_count} added`} />
                        )}
                        {r.updated_count > 0 && (
                            <Chip size="small" label={`${r.updated_count} updated`} />
                        )}
                        {r.failed_count > 0 && (
                            <Chip size="small" color="error" label={`${r.failed_count} failed`} />
                        )}
                    </Box>
                )}
            />

            <FunctionField label="Status" render={() => <StatusChip />} />
        </Datagrid>
    </List>
);

export default { list: ImportList, create: RosterImport };
